/**
 * SquareGameStyleStandard — Clean section renderers for GridEngine.
 * Extracted from SquareGameStyleStandard.jsx layout.
 */
import React from 'react'
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Grid3X3 from 'lucide-react/dist/esm/icons/grid-3x3.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'

/**
 * Standard Hero Section — Clean white card with game title and partner logo
 */
export function SquareStandardHeroSection({ partner, t, editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center">
        {/* Charity Logo */}
        {partner?.partner_logo_url && (
          <div className="flex justify-center mb-6">
            <img
              src={partner.partner_logo_url}
              alt={partner.partner_name || 'Charity Logo'}
              className="h-16 object-contain"
            />
          </div>
        )}

        {/* Game Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          <InlineTranslationEdit
            translationKey="squareGame.title"
            value={c('squareGame.title', 'Square Game')}
            fallback="Square Game"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h1>

        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          <InlineTranslationEdit
            translationKey="squareGame.subtitle"
            value={c('squareGame.subtitle', 'Pick your lucky square and win big while supporting our cause!')}
            fallback="Pick your lucky square and win big while supporting our cause!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>

        <HeroPartnerGlassSelect className="mx-auto mt-6 w-full max-w-md" />
      </div>
    </div>
  )
}

/**
 * Standard Live Draw Section — Draw date + countdown + "Pick Your Square" button
 */
export function SquareStandardLiveDrawSection({
  mainDraw,
  t,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  timeLeft,
  shouldAnimate,
  countdownStyle,
  purchaseNavigate,
}) {
  const { c } = useContent()
  const navigate = purchaseNavigate || usePurchaseNavigate(onLoginClick)

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'Date to be announced'

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          <Calendar className="w-4 h-4" />
          <InlineTranslationEdit
            translationKey="squareGame.drawDate"
            value={c('squareGame.drawDate', 'Draw Date')}
            fallback="Draw Date"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </div>
        <div className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          {drawDateLabel}
        </div>

        {/* Countdown Timer */}
        <div className="mb-6">
          <CountdownTimerStyles
            timeLeft={timeLeft}
            shouldAnimate={shouldAnimate}
            style={countdownStyle}
          />
        </div>

        {/* CTA Button */}
        <button
          onClick={() => navigate('/square-game-purchase')}
          className="w-full py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Grid3X3 className="w-5 h-5" />
          <InlineTranslationEdit
            translationKey="squareGame.pickSquare"
            value={c('squareGame.pickSquare', 'Pick Your Square')}
            fallback="Pick Your Square"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>
      </div>
    </div>
  )
}

/**
 * Standard How It Works Section — 4-step grid (Pick, Purchase, Wait, Win)
 */
export function SquareStandardHowItWorksSection({ mainDraw, t, editMode, handleTranslationUpdate }) {
  const { c } = useContent()
  const totalSquares = mainDraw?.config?.gridSize || 100
  const cols = Math.ceil(Math.sqrt(totalSquares))

  const howItWorksSteps = [
    { icon: Grid3X3, title: 'Pick Your Square', description: `Choose any available square from the ${cols}x${cols} grid`, titleKey: 'SqStandard.step1Title', descKey: 'SqStandard.step1Desc' },
    { icon: DollarSign, title: 'Make Your Purchase', description: 'Each square supports our cause and enters you to win', titleKey: 'SqStandard.step2Title', descKey: 'SqStandard.step2Desc' },
    { icon: Calendar, title: 'Wait for Draw Day', description: 'A winning square is randomly selected on the draw date', titleKey: 'SqStandard.step3Title', descKey: 'SqStandard.step3Desc' },
    { icon: Trophy, title: 'Win the Grand Prize', description: 'If your square is drawn, you win the grand prize!', titleKey: 'SqStandard.step4Title', descKey: 'SqStandard.step4Desc' }
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
        <InlineTranslationEdit
          translationKey="squareGame.howItWorks"
          value={c('squareGame.howItWorks', 'How It Works')}
          fallback="How It Works"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {howItWorksSteps.map((step, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <step.icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                <InlineTranslationEdit translationKey={step.titleKey} value={c(step.titleKey, step.title)} fallback={step.title} onUpdate={handleTranslationUpdate} editMode={editMode} />
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                <InlineTranslationEdit translationKey={step.descKey} value={c(step.descKey, step.description)} fallback={step.description} onUpdate={handleTranslationUpdate} editMode={editMode} />
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
